import { useMemo } from 'react'
import * as THREE from 'three'

/** Um buraco no chão: polígono de pontos (x, z) no mundo. */
export type BuracoNoSolo = ReadonlyArray<readonly [number, number]>

const MARGEM_M = 16
const TAMANHO_MINIMO_M = 40

/**
 * O chão da rua em asfalto, com os buracos dos terrenos escavados (canal,
 * cisterna). Sem buracos a cena usa a pista de sempre; com eles, o chão passa a
 * ser esta chapa, para o asfalto não tampar o que está embaixo.
 */
export function geometriaDoSolo(buracos: readonly BuracoNoSolo[]): THREE.BufferGeometry {
  const pontos = buracos.flat()
  const xs = pontos.map((p) => p[0])
  const zs = pontos.map((p) => p[1])
  const meioX = (Math.min(...xs) + Math.max(...xs)) / 2
  const meioZ = (Math.min(...zs) + Math.max(...zs)) / 2
  const metadeX = Math.max(TAMANHO_MINIMO_M / 2, (Math.max(...xs) - Math.min(...xs)) / 2 + MARGEM_M)
  const metadeZ = Math.max(TAMANHO_MINIMO_M / 2, (Math.max(...zs) - Math.min(...zs)) / 2 + MARGEM_M)

  // a forma vive no plano XY e é deitada com rotateX(-90°): (x, y) vira (x, 0, -y), então y = -z
  const forma = new THREE.Shape()
  forma.moveTo(meioX - metadeX, -(meioZ - metadeZ))
  forma.lineTo(meioX + metadeX, -(meioZ - metadeZ))
  forma.lineTo(meioX + metadeX, -(meioZ + metadeZ))
  forma.lineTo(meioX - metadeX, -(meioZ + metadeZ))
  forma.closePath()
  for (const buraco of buracos) {
    const furo = new THREE.Path()
    buraco.forEach(([x, z], i) => (i === 0 ? furo.moveTo(x, -z) : furo.lineTo(x, -z)))
    furo.closePath()
    forma.holes.push(furo)
  }
  const geometria = new THREE.ShapeGeometry(forma)
  geometria.rotateX(-Math.PI / 2)
  return geometria
}

export function Solo3D({ buracos }: { buracos: readonly BuracoNoSolo[] }) {
  const geometria = useMemo(() => geometriaDoSolo(buracos), [buracos])
  return (
    // sem toque: o clique no chão é do plano invisível da cena, não desta chapa
    <mesh geometry={geometria} position={[0, 0.001, 0]} raycast={() => null} name="solo-asfalto">
      <meshStandardMaterial color="#2b3038" roughness={0.96} metalness={0.03} />
    </mesh>
  )
}

/**
 * Chão de mato, no lugar da pista de asfalto: para os cenários em que a rua fica
 * no topo de um barranco e, embaixo, é encosta com vegetação.
 */
export function SoloMato3D() {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.002, 0]} raycast={() => null} data-solo-mato>
      <planeGeometry args={[120, 120]} />
      <meshStandardMaterial color="#3f5b2a" roughness={1} />
    </mesh>
  )
}
